import { t } from "@i18n";
import {
  AutoCompleteComponentProps,
  BorderStyleTypes,
  ButtonComponentProps,
  CardComponentProps,
  CheckboxComponentProps,
  ComboBoxComponentProps,
  DropdownButtonComponentProps,
  IconComponentProps,
  MaskTypes,
  MultiSelectComponentProps,
  // MultiSelectModesTypes,
  RichTextEditorComponentProps,
  SliderComponentProps,
  SplitButtonComponentProps,
  TextboxComponentProps,
  TextboxMaskComponentProps,
  TextboxNumericComponentProps,
  ToggleSwitchComponentProps,
  TooltipComponentProps,
  DragListComponentProps,
  MapComponentProps,
  StylePropertyListType,
} from "../interface";

const getStylePropertyList = (props: StylePropertyListType): string[] => {
  const { first, last, incBy, prefix = "", postfix = "" } = props;
  const returnArray: any[] = [];
  for (let i = first; i <= last; i = i + incBy) {
    returnArray.push(`${prefix}${i}${postfix}`);
  }
  return returnArray;
};

export const COLORS = {
  WHITE: "white",
  BLACK: "black",
  DEFAULT_GRADIENT_1: "#6BBBE3",
  DEFAULT_GRADIENT_2: "#3E8AB0",
  BTN_GRADIENT_PRIMARY_1: "#6BBBE3",
  BTN_GRADIENT_PRIMARY_2: "#3E8AB0",
  BTN_COLOR_GRADIENT_PRIMARY_1: "#6BBBE3",
  BTN_COLOR_GRADIENT_PRIMARY_2: "#3E8AB0",
  DEFAULT_BG_COLOR: "#6BBBE3",
  BG_LIGHT_BLUE: "#ECF3FE",
  BTN_BG_DISABLED: "#ECF3FE",
  NATURAL_LIGHT_GRAY: "#EAEAEA",
  PRIMARY_TEXT_BLACK: "#192048",
  BTN_COLOR_DISABLED: "rgba(25, 32, 72, 0.5)",
  BTN_BORDER_DISABLED: "#F4F4F4",
  BTN_ACTIVE_BG_COLOR: "#45CF80",
  BTN_INPROGRESS_BG_COLOR: "#FFCF55",
  BTN_INACTIVE_BG_COLOR: "#E75555",
};

export const BUTTON_TYPES = {
  GRADIENT_BUTTON: "gradientButton",
  BACKGROUND_BUTTON: "backgroundButton",
  BORDER_BUTTON: "borderButton",
  TEXT_BUTTON: "textButton",
  ACTIVE_BUTTON: "activeButton",
  IN_ACTIVE_BUTTON: "inActiveButton",
  IN_PROGRESS_BUTTON: "inProgressButton",
  INFO_GRADIENT_BUTTON: "infoGradientButton",
  INFO_BACKGROUND_BUTTON: "infoBackgroundButton",
  SPLIT_GRADIENT_BUTTON: "splitGradientButton",
  SPLIT_BACKGROUND_BUTTON: "splitBackgroundButton",
  DROPDOWN_GRADIENT_BUTTON: "dropdownGradientButton",
  DROPDOWN_BACKGROUND_BUTTON: "dropdownBackgroundButton",
  DROPDOWN_GRADIENT_LIST: "dropdownGradientList",
  DROPDOWN_BACKGROUND_LIST: "dropdownBackgroundList",
  DROPDOWN_BACKGROUND_YELLOW_LIST: "dropdownBackgroundYellowList",
  DROPDOWN_BACKGROUND_GREEN_LIST: "dropdownBackgroundGreenList",
  DROPDOWN_BACKGROUND_RED_LIST: "dropdownBackgroundRedList",
  DROPDOWN_BACKGROUND_ROYAL_BLUE_LIST: "dropdownBackgroundRoyalBlueList",
  DROPDOWN_BACKGROUND_TEXT_LIGHT_LIST: "dropdownBackgroundTextLightList",
};

export const CONSTANTS = {
  NONE: "none",
  AUTO: "auto",
  BLANK: "",
  AVATAR_SIZE: ["SM", "XS", "LG", "XL"],
  BORDER_STYLE: ["double", "solid", "groove", "dotted", "dashed", "ridge", "inset"],
  BORDER_WIDTH: getStylePropertyList({
    first: 1,
    last: 10,
    incBy: 1,
    postfix: "px",
  }),
  BORDER_RADIUS: getStylePropertyList({
    first: 1,
    last: 100,
    incBy: 1,
    postfix: "px",
  }),
  FONT_WEIGHT: getStylePropertyList({
    first: 100,
    last: 900,
    incBy: 100,
  }),
  FONT_SIZE: getStylePropertyList({
    first: 1,
    last: 100,
    incBy: 1,
    postfix: "px",
  }),
  TOOL_TIP_POSITION: [
    "TopLeft",
    "TopCenter",
    "TopRight",
    "BottomLeft",
    "BottomCenter",
    "BottomRight",
    "LeftTop",
    "LeftCenter",
    "LeftBottom",
    "RightTop",
    "RightCenter",
    "RightBottom",
  ],
  WIDTH_IN_PERCENTAGE: getStylePropertyList({
    first: 1,
    last: 100,
    incBy: 1,
    postfix: "%",
  }),
  MIN_VALUE: getStylePropertyList({
    first: 1,
    last: 100,
    incBy: 1,
    postfix: "",
  }),
  MAX_VALUE: getStylePropertyList({
    first: 1,
    last: 100,
    incBy: 1,
    postfix: "",
  }),
  MASK_TYPES: {
    MOBILE: "000-000-0000",
    COUNTRY_ISO_CODE: "LL/LLL",
    DATE: "00/00/0000",
    PRODUCT_KEY: "AAAAA-AAAAA-AAAAA-AAAAA",
    DATE_FORMAT_2: "00/LLL/0000",
    DATE_TYPE_3: "00/LL/00",
  },
  WIDTH: getStylePropertyList({
    first: 1,
    last: 300,
    incBy: 1,
  }),
  HEIGHT: getStylePropertyList({
    first: 1,
    last: 300,
    incBy: 1,
  }),
  BUTTON_TYPE: [
    BUTTON_TYPES.GRADIENT_BUTTON,
    BUTTON_TYPES.BACKGROUND_BUTTON,
    BUTTON_TYPES.BORDER_BUTTON,
    BUTTON_TYPES.TEXT_BUTTON,
    BUTTON_TYPES.ACTIVE_BUTTON,
    BUTTON_TYPES.IN_ACTIVE_BUTTON,
    BUTTON_TYPES.IN_PROGRESS_BUTTON,
    BUTTON_TYPES.INFO_GRADIENT_BUTTON,
    BUTTON_TYPES.INFO_BACKGROUND_BUTTON,
  ],
  SPLIT_BUTTON_TYPE: [BUTTON_TYPES.SPLIT_GRADIENT_BUTTON, BUTTON_TYPES.SPLIT_BACKGROUND_BUTTON],
  DROPDOWN_BUTTON_TYPE: [BUTTON_TYPES.DROPDOWN_GRADIENT_BUTTON, BUTTON_TYPES.DROPDOWN_BACKGROUND_BUTTON],
  TOAST_ICON_TYPE_CLASSES: {
    SUCCESS: {
      PARENT: "e-toast e-toast-success",
      CHILD: "e-toast-success-icon",
    },
    INFO: {
      PARENT: "e-toast e-toast-info",
      CHILD: "e-toast-info-icon",
    },
    WARNING: {
      PARENT: "e-toast e-toast-warning",
      CHILD: "e-toast-warning-icon",
    },
    ERROR: {
      PARENT: "e-toast e-toast-error",
      CHILD: "e-toast-error-icon",
    },
  },
  ORGANIZATION: "organization",
  PROFILE: "profile",
  PORTAL: "portal",
  SIGNUP: "signUpLogo",
};

export const defaultSyncfusionButtonProps: ButtonComponentProps = {
  cssClass: "",
  children: "Primary Btn",
  borderRadius: "0.3125rem",
  borderType: BorderStyleTypes.SOLID,
  fontWeight: "600",
  disabled: false,
};

export const SplitDefaultItems = [
  {
    text: "Paste",
    iconCss: "e-icons e-paste",
  },
  {
    text: "Paste Special",
    iconCss: "e-icons e-paste-special",
  },
  {
    text: "Paste as Formula",
    iconCss: "e-icons e-formula",
  },
  {
    text: "Paste as Hyperlink",
    iconCss: "e-icons e-link",
  },
];

export const defaultSplitButtonProps: SplitButtonComponentProps = {
  content: "Split Button",
  items: SplitDefaultItems,
};
export const defaultDropdownButtonProps: DropdownButtonComponentProps = {
  items: SplitDefaultItems,
  content: "Dropdown Button",
};

export const defaultToggleSwitchButtonProps: ToggleSwitchComponentProps = {
  label: "Toggle Button",
};

export const defaultCheckboxComponentProps: CheckboxComponentProps = {
  checked: true,
  label: "Checkbox",
};
export const defaultRadioButtonComponentProps: CheckboxComponentProps = {
  checked: true,
  label: "Checkbox",
};

export const defaultMultiSelectComponentProps: MultiSelectComponentProps = {
  id: "defaultElement",
  dataSource: [],
  // mode: MultiSelectModesTypes.DEFAULT,
  fields: { text: "game", value: "id" },
  placeholder: t("Favorite Sports"),
  enabled: true,
  widthInPercentage: "100%",
};
export const RichTextEditorTool: string[] = [
  "Bold",
  "Italic",
  "Underline",
  "StrikeThrough",
  "FontName",
  "FontSize",
  "FontColor",
  "BackgroundColor",
  "LowerCase",
  "UpperCase",
  "|",
  "Formats",
  "Alignments",
  "NumberFormatList",
  "BulletFormatList",
  "Outdent",
  "Indent",
  "SuperScript",
  "SubScript",
  "|",
  "CreateTable",
  "CreateLink",
  "Image",
  "FileManager",
  "|",
  "ClearFormat",
  "Print",
  "SourceCode",
  "FullScreen",
  "|",
  "Undo",
  "Redo",
];

export const defaultRichTextEditorComponentProps: RichTextEditorComponentProps = {
  id: "toolsRTE",
  enableResize: false,
  showCharCount: true,
  enabled: true,
  width: "100%",
  items: RichTextEditorTool,
};

export const RichTextEditorHostUrl = "https://ej2-aspcore-service.azurewebsites.net/";
export const defaultTextboxComponentProps: TextboxComponentProps = {
  placeholder: t("Enter Name"),
  widthInPercentage: "100%",
  isSuccess: false,
  isError: false,
  isWarning: false,
  isRTL: false,
  isSmall: false,
  type: "text",
};

export const defaultTextboxMaskComponentProps: TextboxMaskComponentProps = {
  mask: MaskTypes.DATE,
  isDisabled: false,
  widthInPercentage: "100%",
  label: "",
};

export const defaultTextboxNumericComponentProps: TextboxNumericComponentProps = {
  value: 0.5,
  min: 0,
  max: 1,
  step: 0.01,
  isDisabled: false,
  widthInPercentage: "100%",
};

export const defaultAutoCompleteComponentProps: AutoCompleteComponentProps = {
  id: "Games",
  placeholder: t("Eg: Cricket"),
  isEnabled: true,
  widthInPercentage: "100%",
  dataSource: [
    "Badminton",
    "Basketball",
    "Cricket",
    "Football",
    "Golf",
    "Gymnastics",
    "Hockey",
    "Rugby",
    "Snooker",
    "Tennis",
  ],
};

export const defaultComboBoxComponentProps: ComboBoxComponentProps = {
  dataSource: [
    "Badminton",
    "Basketball",
    "Cricket",
    "Football",
    "Golf",
    "Gymnastics",
    "Hockey",
    "Rugby",
    "Snooker",
    "Tennis",
  ],
  placeholder: t("Select Games"),
  popupHeight: "220",
  value: "Football",
  isEnabled: true,
};

export const defaultIconComponentProps: IconComponentProps = {
  name: "access",
  height: "50",
};

export const defaultTooltipComponentProps: TooltipComponentProps = {
  content: t("Tooltip Example"),
  position: "BottomCenter" as any,
  children: <p>{t("Tooltip")}</p>,
};

export const defaultCardComponentProps: CardComponentProps = {
  cardBody: "Card Component",
  cardHeaderText: "Card Header",
  headerLinkText: "View all",
  width: "100%",
  enableMaxMinIcon: true,
  maxMinIconName: "screenmaximize",
  enableShareIcon: true,
  enableThreeDotsIcon: true,
  dataBound: null,
  onClick: null,
  background: "",
  elevation: 1,
};

export const defaultDragListComponentsProps: DragListComponentProps = {
  data1: [
    { Name: "Transmittals", Code: "AU" },
    { Name: "Issues", Code: "BM" },
    { Name: "Portal Settings", Code: "CA" },
    { Name: "Pending Issues", Code: "CM" },
    { Name: "Approved Transmittals", Code: "DK" },
    { Name: "Estimates", Code: "FR" },
    { Name: "Projects Lists", Code: "FI" },
    { Name: "My Project", Code: "DE" },
    { Name: "Pending Issues", Code: "HK" },
  ],
  data2: [
    { Name: "In Review Tasks", Code: "AU" },
    { Name: "Approved Tasks", Code: "BM" },
    { Name: "Pending Projects", Code: "CA" },
    { Name: "Active Issues", Code: "CM" },
    { Name: "IN review tasks", Code: "DK" },
    { Name: "Edit Profile", Code: "FR" },
  ],
  fields: { text: "Name" },
};

export const defaultMapComponentProps: MapComponentProps = {
  latitude: 28.4595,
  longitude: 77.0266,
  zoomFactor: 9,
  enable: false,
  height: "100%",
  width: "100%",
  name: "Delhi",
  isToolTipVisible: true,
};

export const cssStyleColorOther = [
  {
    displayName: "Info Light Blue",
    name: "--info-light-blue",
    colorCode: "#c7e0f4",
  },
  {
    displayName: "Info Lighter Blue",
    name: "--info-lighter-blue",
    colorCode: "#deecf9",
  },
  {
    displayName: "Light Blue",
    name: "--light-blue",
    colorCode: "#c6d0df",
  },
  {
    displayName: "Lighter Gray",
    name: "--lighter-gray",
    colorCode: "#eaeaea",
  },
  {
    displayName: "Disabled Text Gray",
    name: "--disabled-text-gray",
    colorCode: "rgba(25, 32, 72, 0.5)",
  },
  {
    displayName: "Disabled Border",
    name: "--disabled-border",
    colorCode: "#f4f4f4",
  },
  {
    displayName: "Light Gray",
    name: "--light-gray",
    colorCode: "#e2e9f3",
  },
  {
    displayName: "Box Shadow",
    name: "--box-shadow",
    colorCode: "rgba(0, 0, 0, 0.15)",
  },
  {
    displayName: "White Opacity",
    name: "--white-opacity",
    colorCode: "rgba(255, 255, 255, 0.5)",
  },
  {
    displayName: "TextBox Inner Border",
    name: "--inner-border",
    colorCode: "#7bd2fc",
  },
  {
    displayName: "Center Border",
    name: "--center-border",
    colorCode: "#212121",
  },
  {
    displayName: "Border Gray",
    name: "--border-gray",
    colorCode: "#d8d8d8",
  },
  {
    displayName: "Light Black",
    name: "--light-black",
    colorCode: "#333",
  },
  {
    displayName: "Soft Gray",
    name: "--soft-gray",
    colorCode: "#c8c8c8",
  },
];

export const cssStyleColoursPrimary = [
  {
    displayName: "Primary Gradient",
    name: "Primary Gradient",
    colorCode1: "#6bbbe3",
    colorCode2: "#3e8ab0",
    type: "gradient",
  },
  {
    displayName: "Surface Gradient",
    name: "Surface Gradient",
    colorCode1: "FFFFFF",
    colorCode2: "F0F5F9",
    type: "gradient",
  },
  {
    displayName: "Surface Stroke Gradient",
    name: "Surface Stroke Gradient",
    colorCode1: "#ECF3FE",
    colorCode2: "rgb(236, 243, 254, 0.15)",
    type: "gradient",
  },
  {
    displayName: "Primary Gradient Color1",
    name: "--primary-gradient-color1",
    colorCode: "#6bbbe3",
  },
  {
    displayName: "Primary Gradient Color2",
    name: "--primary-gradient-color2",
    colorCode: "#3e8ab0",
  },
  {
    displayName: "Surface Gradient Color1",
    name: "--surface-gradient-color1",
    colorCode: "FFFFFF",
  },
  {
    displayName: "Surface Gradient Color2",
    name: "--surface-gradient-color2",
    colorCode: "F0F5F9",
  },
  {
    displayName: "Surface Stroke Gradient Color1",
    name: "--surface-stroke-gradient-color1",
    colorCode: "rgb(236, 243, 254, 0.15)",
  },
  {
    displayName: "Surface Stroke Gradient Color2",
    name: "--surface-stroke-gradient-color2",
    colorCode: "rgb(236, 243, 254, 0.15)",
  },
  {
    displayName: "Primary Text",
    name: "--primary-text",
    colorCode: "#192048",
  },
  {
    displayName: "Surface Neutral",
    name: "--surface-neutral",
    colorCode: "#f0f5f9",
  },
  {
    displayName: "Surface White",
    name: "--surface-white",
    colorCode: "#ffffff",
  },
  {
    displayName: "Text Light",
    name: "--text-light",
    colorCode: "#666a84",
  },
  {
    displayName: "Selection",
    name: "--selection",
    colorCode: "#caecff",
  },
  {
    displayName: "Button Light",
    name: "--button-light",
    colorCode: "#ecf3fe",
  },
];

export const cssStyleColoursCommunication = [
  {
    displayName: "Positive Communication",
    name: "--positive-communication",
    colorCode: "#45cf80",
  },
  {
    displayName: "Negative Communication",
    name: "--negative-communication",
    colorCode: "#e75555",
  },
  {
    displayName: "Warning",
    name: "--warning",
    colorCode: "#ffcf55",
  },
];

export const defaultAvatarComponentProps = {
  isCircle: false,
  size: "SM",
  profilePicKey: "",
  userName: "Techture Global",
};

export const defaultSliderComponentProps: SliderComponentProps = {
  enabled: true,
  min: 30,
  max: 100,
  value: 30,
  width: 200,
  gradientColor1: "var(--primary-gradient-color1)",
  gradientColor2: "var(--primary-gradient-color2)",
  handleGradientColor1: "var(--primary-gradient-color1)",
  handleGradientColor2: "var(--primary-gradient-color2)",
};

export const errorIcon = '<img class="inline-flex pe-2" src="/assets/icons/WrongIcon.svg" alt="Error Image" />';

export const passwordValidationChecks = {
  required: [true, `${errorIcon} Please enter password.`],
  minLength: [6, `${errorIcon} Password should have at least 6 characters.`],
  regex: [
    /(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+[{\]}\\|;:'",<.>/?]).{6,}/,
    `${errorIcon} Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character.`,
  ],
};

export const userProfileFieldParentWrapperClass: string = "textBoxWrapper";

export const validImageTypes = ["image/jpg", "image/jpeg", "image/png"];
export const validSignDocsTypes = [...validImageTypes, "application/pdf"];

export const fileDefaultStatus = {
  IN_REVIEW: "In review",
  REJECTED: "Rejected",
  APPROVED: "Approved",
};

export const SETTING_RENDER_MODE = {
  READ_ONLY: "read-only",
  EDIT: "edit",
};
